// IndexedDB storage adapter — PSD §11.4, P1.16. Persistence layer.
// Routes records > 64 KB to IndexedDB; small preferences to localStorage.
// Implements StateStorage (string-based) for createJSONStorage.

import type { StateStorage } from 'zustand/middleware';
import { settingsStorage } from './settingsStorage';
import { getDB } from './db';

const LARGE_ITEM_THRESHOLD = 64 * 1024; // 64 KB

async function readFromIDB(name: string): Promise<string | null> {
  try {
    const db = await getDB();
    const row = await db.get('capTableSnapshots', `__state__:${name}`);
    return row ? (row as { value: string }).value : null;
  } catch {
    return null;
  }
}

async function writeToIDB(name: string, value: string): Promise<void> {
  try {
    const db = await getDB();
    await db.put('capTableSnapshots', { id: `__state__:${name}`, value });
  } catch {
    // quota / unavailable — caller surfaces toast
  }
}

/** StateStorage that routes large state to IDB and small prefs to localStorage. */
export const idbStateStorage: StateStorage = {
  getItem: async (name: string): Promise<string | null> => {
    const small = settingsStorage.getItem(name);
    if (small !== null) {
      // marker for IDB offload, or a small inline payload
      if (small === '__idb__') {
        return readFromIDB(name);
      }
      return small;
    }
    return readFromIDB(name);
  },
  setItem: async (name: string, value: string): Promise<void> => {
    if (value.length < LARGE_ITEM_THRESHOLD) {
      settingsStorage.setItem(name, value);
    } else {
      settingsStorage.setItem(name, '__idb__');
      await writeToIDB(name, value);
    }
  },
  removeItem: async (name: string): Promise<void> => {
    settingsStorage.removeItem(name);
    try {
      const db = await getDB();
      await db.delete('capTableSnapshots', `__state__:${name}`);
    } catch {
      // ignore
    }
  },
};
